/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-09-30
 */
import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { claveDePreferencia, guardarPreferencia, leerPreferencia } from 'nebula-ui-kit';
import { TabIdService } from '../tab-container/tab-id.service';
import { FavoritoRemoto, FavoritosApi } from './favoritos.api';

/** Espacio de nombres de los favoritos entre las preferencias del usuario. */
const PREFIJO = 'favoritos:';

/** Estado HTTP con que masters avisa que la pantalla ya era favorita. */
const YA_ERA_FAVORITA = 409;

/** Estado HTTP de un favorito que masters ya no tiene. */
const NO_EXISTE = 404;

/** Lo marcado o quitado mientras masters no respondia. */
interface Pendientes {
  altas: string[];
  bajas: string[];
}

/**
 * Las pantallas que el usuario marco como favoritas.
 *
 * <p>Viven en nebula-masters, a nombre del usuario por su username (orden de Carlos,
 * 1-oct-2026): viajan con el usuario entre equipos. El navegador guarda una COPIA a su nombre,
 * que pinta el menu al instante y lo sostiene cuando masters no responde. Manda el servidor: lo
 * que el servidor devuelve reemplaza la copia.</p>
 *
 * <p>Lo que se marca o se quita mientras masters no responde se conserva en el navegador como
 * PENDIENTE y se aplica en cuanto responde. Lo que masters rechaza se deshace. Los favoritos que
 * el navegador ya tenia de antes se suben UNA vez, la primera vez que responde.</p>
 *
 * <p>De cada pantalla se guarda solo su CLAVE, la que se deriva de su ruta
 * ({@link TabIdService#claveDeRuta}). El nombre y el icono se toman del menu al pintarlo: si el
 * menu cambia un rotulo o el idioma, el favorito lo sigue.</p>
 */
@Injectable({ providedIn: 'root' })
export class FavoritosService {
  private readonly tabIds = inject(TabIdService);
  private readonly router = inject(Router);
  private readonly api = inject(FavoritosApi);

  /** Clave de la ruta que se esta mostrando. */
  private readonly rutaActual = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => this.claveDe(e.urlAfterRedirects)),
    ),
    { initialValue: this.claveDe(this.router.url) },
  );

  /** Claves de las pantallas favoritas, en su orden. */
  private readonly lista = signal<string[]>(this.leerCopia());

  /** Identificador en masters de cada favorito, por su clave: lo necesita quitarlo. */
  private readonly ids = new Map<string, number>();

  /** Se mueve en cada lectura del servidor, para descartar respuestas de un dueño anterior. */
  private turno = 0;

  /** Claves de las pantallas favoritas, en su orden. */
  readonly claves = computed<string[]>(() => this.lista());

  /**
   * Clave de la pantalla que se esta mostrando, si admite favorito; la pone y la quita la
   * pantalla (ver `NebulaSmartCrudComponent.permiteFavorito`).
   */
  readonly pantallaActual = signal<string | null>(null);

  /**
   * Si la estrella se ofrece: hay una pantalla que la admite y es la que se esta mostrando. Se
   * compara con la ruta para no depender de que la pantalla anterior la haya retirado al salir.
   */
  readonly ofrecida = computed(() => {
    const actual = this.pantallaActual();
    return !!actual && actual === this.rutaActual();
  });

  /** Si la pantalla que se esta mostrando es favorita. */
  readonly actualEsFavorita = computed(() => {
    const actual = this.pantallaActual();
    return !!actual && this.claves().includes(actual);
  });

  /**
   * Clave de una ruta, con la misma regla de las pestañas.
   *
   * @param ruta ruta de la pantalla.
   * @returns su clave.
   */
  claveDe(ruta: string | undefined | null): string {
    return this.tabIds.claveDeRuta(ruta);
  }

  /**
   * Si una ruta es favorita.
   *
   * @param ruta ruta de la pantalla.
   * @returns si esta marcada.
   */
  esFavorita(ruta: string | undefined | null): boolean {
    const clave = this.claveDe(ruta);
    return !!clave && this.claves().includes(clave);
  }

  /** Agrega o quita de favoritos la pantalla que se esta mostrando. */
  alternarActual(): void {
    const actual = this.pantallaActual();
    if (!actual) return;
    if (this.claves().includes(actual)) {
      this.quitarClave(actual);
    } else {
      this.agregarClave(actual);
    }
  }

  /**
   * Quita una pantalla de favoritos, sea o no la que se esta mostrando (desde el menu).
   *
   * @param ruta ruta de la pantalla.
   */
  quitar(ruta: string | undefined | null): void {
    const clave = this.claveDe(ruta);
    if (!clave || !this.claves().includes(clave)) return;
    this.quitarClave(clave);
  }

  /**
   * Vuelve a leer los favoritos: al entrar un usuario, los suyos.
   *
   * <p>Pinta primero la copia del navegador y luego la reemplaza con lo que devuelva masters. Si
   * masters no responde, queda la copia.</p>
   */
  recargar(): void {
    this.ids.clear();
    this.lista.set(this.leerCopia());
    if (!this.clave()) return;

    const turno = ++this.turno;
    this.api.listar().subscribe({
      next: (pagina) => {
        if (turno !== this.turno) return;
        this.adoptar(pagina?.content ?? []);
      },
      // Sin servidor queda la copia del navegador.
      error: () => undefined,
    });
  }

  /**
   * Marca una pantalla: se ve al instante y se registra en masters.
   *
   * @param clave clave de la pantalla.
   */
  private agregarClave(clave: string): void {
    const antes = this.claves();
    this.fijar([...antes, clave]);
    this.subir(clave, antes.length + 1, () => this.fijar(antes));
  }

  /**
   * Quita una pantalla: se ve al instante y se elimina en masters.
   *
   * @param clave clave de la pantalla.
   */
  private quitarClave(clave: string): void {
    const antes = this.claves();
    this.fijar(antes.filter((c) => c !== clave));
    const id = this.ids.get(clave);
    if (id == null) {
      // Masters no ha respondido en esta sesion: la baja espera a que responda.
      this.anotarPendiente(clave, 'baja');
      return;
    }
    this.bajar(clave, id, () => this.fijar(antes));
  }

  /**
   * Registra un favorito en masters.
   *
   * <p>Si masters ya lo tenia, se relee. Si lo rechaza, se deshace. Si no responde, queda
   * pendiente en el navegador y se sube cuando responda.</p>
   *
   * @param clave clave de la pantalla.
   * @param orden su posicion en la lista.
   * @param deshacer como se deshace en pantalla si masters lo rechaza.
   */
  private subir(clave: string, orden: number, deshacer: () => void): void {
    this.api.create({ ruta: clave, orden }).subscribe({
      next: (creado) => {
        if (creado?.id != null) this.ids.set(clave, creado.id);
        this.anotarPendiente(clave, null);
      },
      error: (err) => {
        const estado = this.estado(err);
        if (estado === YA_ERA_FAVORITA) {
          this.anotarPendiente(clave, null);
          this.recargar();
        } else if (this.esRechazo(estado)) {
          this.anotarPendiente(clave, null);
          deshacer();
        } else {
          this.anotarPendiente(clave, 'alta');
        }
      },
    });
  }

  /**
   * Elimina un favorito en masters.
   *
   * <p>Si masters ya no lo tenia, la baja esta hecha. Si la rechaza, se deshace. Si no responde,
   * queda pendiente en el navegador y se aplica cuando responda.</p>
   *
   * @param clave clave de la pantalla.
   * @param id su identificador en masters.
   * @param deshacer como se deshace en pantalla si masters lo rechaza.
   */
  private bajar(clave: string, id: number, deshacer: () => void): void {
    this.api.delete(id).subscribe({
      next: () => {
        this.ids.delete(clave);
        this.anotarPendiente(clave, null);
      },
      error: (err) => {
        const estado = this.estado(err);
        if (estado === NO_EXISTE) {
          this.ids.delete(clave);
          this.anotarPendiente(clave, null);
        } else if (this.esRechazo(estado)) {
          this.anotarPendiente(clave, null);
          deshacer();
        } else {
          this.anotarPendiente(clave, 'baja');
        }
      },
    });
  }

  /**
   * Toma lo que devolvio masters como la verdad y le aplica lo que quedo pendiente en el
   * navegador: lo marcado o quitado mientras masters no respondia y, una sola vez, los favoritos
   * que el navegador tenia de antes.
   *
   * @param remotos los favoritos del usuario en masters.
   */
  private adoptar(remotos: FavoritoRemoto[]): void {
    this.ids.clear();
    const servidor: string[] = [];
    for (const f of remotos) {
      if (!f?.ruta || servidor.includes(f.ruta)) continue;
      servidor.push(f.ruta);
      this.ids.set(f.ruta, f.id);
    }

    const pendientes = this.leerPendientes();
    const marca = this.claveSubidos();
    const altas =
      marca && leerPreferencia<boolean>(marca) !== true
        ? [...new Set([...pendientes.altas, ...this.leerCopia()])]
        : pendientes.altas;
    if (marca) guardarPreferencia(marca, true);

    const nuevas = altas.filter((c) => !servidor.includes(c) && !pendientes.bajas.includes(c));
    const viejas = pendientes.bajas.filter((c) => servidor.includes(c));
    this.guardarPendientes({ altas: [], bajas: [] });

    this.fijar([...servidor.filter((c) => !viejas.includes(c)), ...nuevas]);
    nuevas.forEach((clave, i) =>
      this.subir(clave, servidor.length + i + 1, () =>
        this.fijar(this.claves().filter((c) => c !== clave)),
      ),
    );
    viejas.forEach((clave) =>
      this.bajar(clave, this.ids.get(clave) as number, () => this.fijar([...this.claves(), clave])),
    );
  }

  /**
   * Anota, o borra, lo que una pantalla tiene pendiente con masters.
   *
   * @param clave clave de la pantalla.
   * @param que si quedo pendiente de alta o de baja; nada si ya no tiene pendiente.
   */
  private anotarPendiente(clave: string, que: 'alta' | 'baja' | null): void {
    const p = this.leerPendientes();
    const altas = p.altas.filter((c) => c !== clave);
    const bajas = p.bajas.filter((c) => c !== clave);
    if (que === 'alta') altas.push(clave);
    if (que === 'baja') bajas.push(clave);
    this.guardarPendientes({ altas, bajas });
  }

  /** Lo pendiente con masters, a nombre del usuario; lo que no se entiende se ignora. */
  private leerPendientes(): Pendientes {
    const clave = this.claveDePendientes();
    const p = clave ? leerPreferencia<Partial<Pendientes>>(clave) : null;
    const lista = (v: unknown): string[] =>
      Array.isArray(v) ? v.filter((c) => typeof c === 'string') : [];
    return { altas: lista(p?.altas), bajas: lista(p?.bajas) };
  }

  /**
   * Guarda lo pendiente con masters.
   *
   * @param p altas y bajas pendientes.
   */
  private guardarPendientes(p: Pendientes): void {
    const clave = this.claveDePendientes();
    if (clave) guardarPreferencia(clave, p);
  }

  /**
   * Si masters RECHAZO la operacion, a diferencia de no haber respondido: un 4xx que no sea de
   * sesion vencida, recurso inexistente o espera.
   *
   * @param estado estado HTTP de la respuesta.
   * @returns si fue un rechazo.
   */
  private esRechazo(estado: number): boolean {
    return estado >= 400 && estado < 500 && ![401, 404, 408, 429].includes(estado);
  }

  /**
   * Deja la lista en pantalla y su copia en el navegador.
   *
   * @param claves las claves de las pantallas favoritas.
   */
  private fijar(claves: string[]): void {
    this.lista.set(claves);
    const clave = this.clave();
    if (clave) guardarPreferencia(clave, claves);
  }

  /** La copia del navegador, a nombre del usuario; lo que no se entiende se ignora. */
  private leerCopia(): string[] {
    const clave = this.clave();
    const guardadas = clave ? leerPreferencia<unknown>(clave) : null;
    return Array.isArray(guardadas) ? guardadas.filter((c) => typeof c === 'string') : [];
  }

  /**
   * Estado HTTP de un error, venga crudo o envuelto.
   *
   * @param err el error.
   * @returns su estado, o 0 si no lo trae.
   */
  private estado(err: unknown): number {
    const e = err as { status?: number; error?: { status?: number } } | null;
    return e?.status ?? e?.error?.status ?? 0;
  }

  /** Donde se guarda la copia, a nombre del usuario; sin usuario no hay donde. */
  private clave(): string | null {
    return claveDePreferencia(PREFIJO, 'pantallas');
  }

  /** Donde se guarda lo pendiente con masters, a nombre del usuario. */
  private claveDePendientes(): string | null {
    return claveDePreferencia(PREFIJO, 'pendientes');
  }

  /** Marca de que los favoritos del navegador ya se subieron a masters, a nombre del usuario. */
  private claveSubidos(): string | null {
    return claveDePreferencia(PREFIJO, 'subidos');
  }
}
